import { ArrowDropDown, ArrowDropUp } from '@tempo-io/sui-icons';
import { type ReactNode, useId, useRef, useState } from 'react';
import type { TypeTokens } from '../tokens';
import { useDismissOnOutside } from './useDismissOnOutside';

interface InlineMenuOption {
  id: string;
  label: string;
  /** A second line — a role's lens, a run's model, or (with `inline`) a date beside the label. */
  detail?: string;
  /** Renders `detail` beside `label` instead of on its own line, for a row that pairs a number with a date. */
  inline?: boolean;
  /** Shown at the end of the row: "core", "in_progress". */
  tag?: string;
  /** Tints `tag` as an accent pill instead of the plain meta idiom, for a highlight like "Latest". */
  tagTone?: 'accent';
  selected?: boolean;
}

/**
 * A clickable section heading inside the menu, grouping the options that follow it until the next
 * header. Picking it acts like picking a value — `onSelect` gets its `id` — so a caller can point a
 * header at whichever option it should stand in for (typically the group's own first row).
 */
interface InlineMenuHeader {
  kind: 'header';
  id: string;
  label: string;
  /** Shown on hover, to the right of the label — a "go to latest" affordance. */
  hint?: string;
}

type InlineMenuRow = InlineMenuOption | InlineMenuHeader;

interface InlineMenuProps {
  type: Pick<TypeTokens, 'body' | 'meta' | 'section'>;
  /** The word or phrase inside the run sentence that opens this menu. */
  trigger: string;
  /**
   * `sentence` (the default) is an underlined phrase inside the run sentence. `chip` is a bordered
   * button with a leading icon and a caret — the filter idiom Loop Intelligence uses for its Loop
   * scope, borrowed so a reader meets one filter control across the product.
   */
  variant?: 'sentence' | 'chip';
  /** Drawn at the head of a `chip` trigger. */
  icon?: ReactNode;
  /**
   * How loud a `sentence` trigger is. `strong` marks it as the variable part of the statement;
   * `inherit` lets it take the sentence's own colour and weight, so the whole line recedes together.
   */
  emphasis?: 'strong' | 'inherit';
  /** Screen-reader label, since the trigger alone reads as a fragment of a sentence. */
  ariaLabel: string;
  options: InlineMenuRow[];
  onSelect: (id: string) => void;
  /** A single action at the foot of the menu — "Manage roles", "Read the latest run". */
  footer?: { label: string; onSelect: () => void };
  emptyLabel?: string;
  testId?: string;
  children?: ReactNode;
}

/**
 * A control that reads as part of a sentence. The header states the run in plain language and the
 * variable parts of that statement are these — click the role to change role, click the date to
 * change run. Underlined rather than boxed, so the header carries no buttons and never reads as a
 * toolbar.
 *
 * Written here rather than taken from a component library: this surface deliberately shares no UI
 * with the classic feed, and a popover is the one primitive it cannot do without.
 */
export const InlineMenu = ({
  type,
  trigger,
  variant = 'sentence',
  icon,
  emphasis = 'strong',
  ariaLabel,
  options,
  onSelect,
  footer,
  emptyLabel,
  testId,
}: InlineMenuProps) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);
  const panelId = useId();

  useDismissOnOutside(open, containerRef, () => setOpen(false));

  return (
    <span ref={containerRef} className="relative inline-block">
      <button
        type="button"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? panelId : undefined}
        data-testid={testId}
        onClick={() => setOpen(!open)}
        className={
          variant === 'chip'
            ? 'flex cursor-pointer items-center gap-1.5 rounded-lg border border-border-neutral bg-bg-surface-base px-3 py-1.5 text-sm text-text-neutral-primary transition-colors hover:bg-bg-neutral-subtle-hovered focus-visible:outline focus-visible:outline-2 focus-visible:outline-border-brand'
            : `cursor-pointer underline decoration-border-neutral-bold decoration-1 underline-offset-4 hover:decoration-text-brand-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-border-brand ${
                emphasis === 'strong' ? 'font-medium text-text-neutral-primary' : ''
              }`
        }
      >
        {variant === 'chip' ? icon : null}
        {trigger}
        {variant === 'chip' ? (
          open ? (
            <ArrowDropUp className="size-4 shrink-0" aria-hidden="true" />
          ) : (
            <ArrowDropDown className="size-4 shrink-0" aria-hidden="true" />
          )
        ) : null}
      </button>
      {open ? (
        <span
          // A listbox rather than a menu: every one of these picks a value rather than running a
          // command, and the footer action is marked separately below.
          id={panelId}
          role="listbox"
          aria-label={ariaLabel}
          className="absolute top-full left-0 z-20 mt-2 flex max-h-80 w-80 flex-col overflow-y-auto rounded-md border border-border-neutral bg-bg-surface-overlay py-1 shadow-lg"
        >
          {options.length === 0 && emptyLabel ? (
            <span className={`px-3 py-2 ${type.body}`}>{emptyLabel}</span>
          ) : null}
          {options.map((row, index) =>
            'kind' in row ? (
              <span key={`header-${row.id}`} className="contents">
                {index > 0 ? (
                  <span className="my-1 h-px bg-border-neutral-subtle" aria-hidden="true" />
                ) : null}
                <button
                  type="button"
                  data-testid="agentic-x-menu-header"
                  onClick={() => {
                    onSelect(row.id);
                    setOpen(false);
                  }}
                  className={`group flex items-center justify-between gap-2 px-3 py-1.5 text-left transition-colors hover:text-text-brand-primary ${type.section}`}
                >
                  <span>{row.label}</span>
                  {row.hint ? (
                    <span className="hidden text-text-brand-primary group-hover:inline">
                      {row.hint}
                    </span>
                  ) : null}
                </button>
              </span>
            ) : (
              <button
                key={row.id}
                type="button"
                role="option"
                aria-selected={Boolean(row.selected)}
                data-testid="agentic-x-menu-option"
                onClick={() => {
                  onSelect(row.id);
                  setOpen(false);
                }}
                className={`flex flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-bg-neutral-subtle-hovered ${
                  row.selected ? 'bg-bg-brand-subtler' : ''
                }`}
              >
                <span className="flex w-full items-baseline justify-between gap-2">
                  <span className="flex items-baseline gap-2">
                    <span
                      className={`text-sm font-medium ${row.selected ? 'text-text-brand-primary' : 'text-text-neutral-primary'}`}
                    >
                      {row.label}
                    </span>
                    {row.inline && row.detail ? (
                      <span className="text-xs text-text-neutral-secondary">{row.detail}</span>
                    ) : null}
                  </span>
                  {row.tag ? (
                    <span
                      className={
                        row.tagTone === 'accent'
                          ? `rounded-full bg-bg-brand-subtler px-2 py-0.5 text-text-brand-primary ${type.meta}`
                          : type.meta
                      }
                    >
                      {row.tag}
                    </span>
                  ) : null}
                </span>
                {!row.inline && row.detail ? (
                  <span className="text-xs leading-snug text-text-neutral-secondary">
                    {row.detail}
                  </span>
                ) : null}
              </button>
            ),
          )}
          {footer ? (
            <>
              <span className="my-1 h-px bg-border-neutral-subtle" aria-hidden="true" />
              <button
                type="button"
                data-testid="agentic-x-menu-footer"
                onClick={() => {
                  footer.onSelect();
                  setOpen(false);
                }}
                className={`px-3 py-2 text-left hover:bg-bg-neutral-subtle-hovered ${type.meta} hover:text-text-brand-primary`}
              >
                {footer.label}
              </button>
            </>
          ) : null}
        </span>
      ) : null}
    </span>
  );
};
